import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { AdminLayout } from "@/pages/admin/AdminLayout";
import { toast } from "sonner";
import { Users, FileText, CreditCard, GraduationCap, Mail, ShieldAlert, Activity, CheckCircle2, XCircle, TrendingUp, Bot } from "lucide-react";

function Kpi({ icon: Ic, label, value, sub }) {
  return (
    <div className="border border-white/10 rounded-sm p-4 bg-white/5" data-testid={`kpi-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="flex items-center gap-2 text-paper/40 text-xs"><Ic className="h-4 w-4" /> {label}</div>
      <div className="font-serif text-3xl mt-1">{value}</div>
      {sub && <div className="text-xs text-paper/40 mt-0.5">{sub}</div>}
    </div>
  );
}

export default function AdminAutopilot() {
  const [ov, setOv] = useState(null);
  const [feed, setFeed] = useState([]);
  const [approvals, setApprovals] = useState([]);
  const [roles, setRoles] = useState({});
  const [tab, setTab] = useState("overview");

  const load = () => {
    api.get("/admin/autopilot/overview").then((r) => setOv(r.data));
    api.get("/admin/autopilot/activity").then((r) => setFeed(r.data));
    api.get("/admin/autopilot/approvals").then((r) => setApprovals(r.data));
    api.get("/admin/autopilot/roles").then((r) => setRoles(r.data));
  };
  useEffect(() => { load(); }, []);

  const decide = async (apid, action) => {
    try { await api.post(`/admin/autopilot/approvals/${apid}/${action}`, { note: "" }); toast.success(action === "approve" ? "Approved & executed" : "Rejected"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <AdminLayout>
      <div className="p-8 max-w-7xl" data-testid="admin-autopilot-page">
        <div className="flex items-center gap-3">
          <Bot className="h-7 w-7 text-heraldic" />
          <div><h1 className="font-serif text-3xl">Executive Control Centre</h1><p className="text-paper/50 text-sm mt-1">Simo Autopilot runs the routine. You manage exceptions and decisions.</p></div>
        </div>

        <div className="mt-5 flex gap-1 border-b border-white/10">
          {[["overview", "Overview"], ["approvals", `Approvals${approvals.length ? ` (${approvals.length})` : ""}`], ["activity", "AI Activity"], ["workforce", "Workforce"]].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`autopilot-tab-${k}`} className={`px-4 py-2.5 text-sm border-b-2 -mb-px ${tab === k ? "border-heraldic text-paper" : "border-transparent text-paper/50"}`}>{l}</button>
          ))}
        </div>

        {tab === "overview" && ov && (
          <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="autopilot-kpis">
            <Kpi icon={Users} label="Leads today" value={ov.leads_today} />
            <Kpi icon={FileText} label="Application starts" value={ov.application_starts_today} sub="today" />
            <Kpi icon={XCircle} label="Abandoned" value={ov.abandoned} sub="started, unpaid" />
            <Kpi icon={CreditCard} label="Revenue" value={`€${ov.revenue_eur}`} sub={`${ov.paid} paid`} />
            <Kpi icon={Activity} label="Assessments" value={ov.assessments} />
            <Kpi icon={GraduationCap} label="Offers / Enrolled" value={`${ov.offers} / ${ov.enrolments}`} />
            <Kpi icon={Mail} label="Campaign sends" value={ov.campaign.sent} sub={`${ov.campaign.opened} opened · ${ov.campaign.clicked} clicked`} />
            <Kpi icon={Mail} label="Unanswered comms" value={ov.unanswered_comms} />
            <Kpi icon={ShieldAlert} label="Awaiting approval" value={ov.pending_approvals} />
            <Kpi icon={Bot} label="AI actions today" value={ov.ai_actions_today} />
          </div>
        )}

        {tab === "approvals" && (
          <div className="mt-6 space-y-3" data-testid="autopilot-approvals">
            {approvals.length === 0 && <div className="text-paper/40 text-sm py-8 text-center">No actions awaiting approval. Autopilot is handling the routine.</div>}
            {approvals.map((a) => (
              <div key={a.apid} className="border border-amber-500/30 bg-amber-500/5 rounded-sm p-4" data-testid={`approval-${a.apid}`}>
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="text-sm font-medium flex items-center gap-2"><ShieldAlert className="h-4 w-4 text-amber-400" /> {a.tool}</div>
                    <div className="text-xs text-paper/50 mt-1">“{a.prompt}”</div>
                    <div className="text-xs text-paper/40 mt-1">{a.reasoning}</div>
                    <pre className="text-[10px] text-paper/40 mt-2 bg-black/30 p-2 rounded-sm overflow-auto">{JSON.stringify(a.args, null, 1)}</pre>
                  </div>
                  <div className="flex flex-col gap-2 shrink-0">
                    <button onClick={() => decide(a.apid, "approve")} data-testid={`approve-${a.apid}`} className="inline-flex items-center gap-1.5 bg-emerald-600 text-white px-3 py-1.5 rounded-sm text-xs"><CheckCircle2 className="h-3.5 w-3.5" /> Approve</button>
                    <button onClick={() => decide(a.apid, "reject")} data-testid={`reject-${a.apid}`} className="inline-flex items-center gap-1.5 border border-white/15 text-red-400 px-3 py-1.5 rounded-sm text-xs"><XCircle className="h-3.5 w-3.5" /> Reject</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "activity" && (
          <div className="mt-6" data-testid="autopilot-activity">
            <div className="border border-white/10 rounded-sm divide-y divide-white/5">
              {feed.length === 0 && <div className="text-paper/40 text-sm py-8 text-center">No AI activity yet. Use “Ask Simo AI” on any page.</div>}
              {feed.map((f) => (
                <div key={f.aid} className="px-4 py-3 flex items-center gap-3 text-sm">
                  <span className={`h-2 w-2 rounded-full shrink-0 ${f.status === "executed" ? "bg-emerald-500" : f.status === "awaiting_approval" ? "bg-amber-500" : f.status === "rejected" ? "bg-red-500" : "bg-slate-500"}`} />
                  <span className="text-heraldic text-xs w-28 shrink-0">{f.role}</span>
                  <span className="flex-1 text-paper/70">{f.action}</span>
                  <span className="text-[10px] text-paper/30 shrink-0">{(f.ts || "").slice(0, 16).replace("T", " ")}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "workforce" && (
          <div className="mt-6 grid md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="autopilot-workforce">
            {Object.entries(roles).map(([div, list]) => (
              <div key={div} className="border border-white/10 rounded-sm p-4 bg-white/5">
                <div className="text-xs uppercase tracking-wide text-heraldic mb-3">{div}</div>
                <div className="space-y-2">
                  {list.map((r) => (
                    <div key={r.name} className="text-sm">
                      <div className="flex items-center justify-between"><span>{r.name}</span><span className={`text-[9px] px-1.5 py-0.5 rounded-full ${r.autonomy === "high" ? "bg-red-500/20 text-red-300" : r.autonomy === "controlled" ? "bg-amber-500/20 text-amber-300" : "bg-emerald-500/20 text-emerald-300"}`}>{r.autonomy}</span></div>
                      <div className="text-[11px] text-paper/40">{r.objective}</div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
